import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Divider,
  Stack,
  Button,
  Paper,
  Badge,
} from "@mui/material";
import {
  AccessTime,
  LocationOn,
  CheckCircle,
  LocalHospital,
  WorkOutline,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDoctorById } from "../../Redux/Slice/DoctorSlice";
import docImg from "../../assests/doctor.jpg";
import SEO from "../../SEO";

const DoctorCardDetails = ({ doctorId, specialityId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { doctorById } = useSelector((state) => state.doctor);
  const doctor = doctorById?.data;

  useEffect(() => {
    const idToFetch = doctorId || id;
    if (idToFetch) {
      dispatch(getDoctorById(idToFetch));
    }
  }, [doctorId, id, dispatch]);

  if (!doctor) return null;

  const handleBookClick = () => {
    navigate(`/booking/${doctorId}/${specialityId}`);
  };

  return (
    <>
      <SEO
        title={`${doctor.name} – ${doctor.speciality} | Trust Doc`}
        description={doctor.shortBio}
        canonical={`https://www.trust-doc.com/doctor-details/${id}`}
        image={doctor.profileImage}
      />
      <Card
        sx={{
          maxWidth: 650,
          borderRadius: 3,
          mx: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          overflow: "hidden",
          mt: 3,
        }}
      >
        {/* Header Section */}
        <Box display="flex" p={3} alignItems="center" bgcolor="#f0f4f8">
          <Badge
            overlap="circular"
            badgeContent={<CheckCircle color="primary" />}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <CardMedia
              component="img"
              image={doctor.doctorImage || docImg}
              alt={doctor.doctorName}
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #1976d2",
              }}
            />
          </Badge>

          <Box ml={3}>
            <Typography variant="h6" fontWeight={700}>
              {doctor.doctorName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {doctor.designation}
            </Typography>
            <Box mt={1}>
              <Chip
                label={`${doctor.experience} Years Experience`}
                color="success"
                size="small"
                icon={<WorkOutline />}
                sx={{ mr: 1 }}
              />
              <Chip
                label={doctor.specialityName}
                variant="outlined"
                color="primary"
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Content Section */}
        <CardContent sx={{ p: 3 }}>
          {/* Hospital Info */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f9fbfc", mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <LocalHospital color="action" />
              <Box>
                <Typography fontWeight={600} variant="body2">
                  {doctor.hospitalName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hospital Name
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Location Info */}
          {doctor.hospitalAddress && (
            <Paper
              elevation={0}
              sx={{ p: 2, backgroundColor: "#f9fbfc", mb: 2 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <LocationOn color="action" />
                <Box>
                  <Typography fontWeight={600} variant="body2">
                    {doctor.hospitalAddress}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Hospital Address
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Schedules */}
          {doctor.schedules && doctor.schedules.length > 0 && (
            <Paper
              elevation={0}
              sx={{ p: 2, backgroundColor: "#f9fbfc", mb: 2 }}
            >
              <Typography fontWeight={600} sx={{ mb: 1 }}>
                Available Schedules
              </Typography>
              <Box>
                {doctor.schedules.map((schedule, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 1 }}
                  >
                    <AccessTime color="action" />
                    <Typography variant="body2">
                      <strong>{schedule.days}</strong>:{" "}
                      {schedule.startTime?.slice(0, 5)} -{" "}
                      {schedule.endTime?.slice(0, 5)}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Paper>
          )}
        </CardContent>

        <Divider />

        {/* Footer */}
        <Box p={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleBookClick}
            sx={{ py: 1.3, fontWeight: 600 }}
          >
            Book Appointment
          </Button>
        </Box>
      </Card>
    </>
  );
};

export default DoctorCardDetails;
